/**
 * App-JWT token manager.
 *
 *  - In-memory cache is the source of truth.
 *  - localStorage is the persistence layer so full-page reloads (OAuth
 *    callbacks, Plaid OAuth bounces, Stripe checkout, bookmarks) don't
 *    wipe the session. Gated by PERSIST_TOKEN_ACROSS_RELOADS.
 *  - Single-flight silent refresh (POST /api/auth/refresh) so concurrent
 *    callers don't stampede.
 *  - Schedules the next refresh 60s before exp; min 5s floor.
 *  - tab_close session policy: if the user picked "sign out on tab close",
 *    we clear the token when the session sentinel is missing on boot.
 *  - BroadcastChannel('bk-auth') propagates rotated tokens across tabs.
 */

const STORAGE_KEY = 'bk.app_jwt'
// Legacy key kept on reads for users with an old pre-migration localStorage
// entry. Writes go to both keys so a user who opens an old bookmark during
// deploy doesn't get logged out.
const LEGACY_STORAGE_KEY = 'token'
const SESSION_TIMEOUT_KEY = 'bk.session_timeout'
const TAB_ALIVE_KEY = 'bk.tab_alive'
const SUPER_ADMIN_TOKEN_KEY = 'bk.super_admin_token'

export const PLATFORM_TENANT_ID = '00000000-0000-0000-0000-000000000001'

const REFRESH_LEAD_SECONDS = 60
const MIN_SCHEDULE_SECONDS = 5
const PERSIST_TOKEN_ACROSS_RELOADS = true

export type DecodedJwt = {
  sub?: string
  userId?: string
  email?: string
  tenant_id?: string
  role?: string
  permissions?: string[]
  is_impersonating?: boolean
  original_sub?: string
  exp?: number
}

export type AuthUser = {
  userId: string
  email: string
  tenantId: string
  role: string
  permissions: string[]
  isImpersonating: boolean
  originalSub?: string
  exp?: number
}

type Listener = (token: string | null) => void

let inMemoryToken: string | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null
let refreshInFlight: Promise<boolean> | null = null
const listeners = new Set<Listener>()

let broadcast: BroadcastChannel | null = null
try {
  broadcast = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('bk-auth') : null
  broadcast?.addEventListener('message', (ev) => {
    if (!ev?.data || ev.data.type !== 'token') return
    const next = ev.data.token as string | null
    if (next === inMemoryToken) return
    inMemoryToken = next
    notify()
    scheduleRefresh()
  })
} catch {
  /* unsupported environment */
}

function notify() {
  for (const fn of listeners) fn(inMemoryToken)
}

function readPersisted(): string | null {
  if (!PERSIST_TOKEN_ACROSS_RELOADS) return null
  try {
    return (
      localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY) || null
    )
  } catch {
    return null
  }
}

function writePersisted(token: string | null): void {
  if (!PERSIST_TOKEN_ACROSS_RELOADS) return
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEY, token)
      localStorage.setItem(LEGACY_STORAGE_KEY, token)
    } else {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(LEGACY_STORAGE_KEY)
    }
  } catch {
    /* quota / blocked */
  }
}

function decode(token: string | null): DecodedJwt | null {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    return JSON.parse(atob(padded)) as DecodedJwt
  } catch {
    return null
  }
}

export const tokenManager = {
  /** Boot - call once at app startup to seed from localStorage. */
  bootstrap(): void {
    inMemoryToken = readPersisted()
    notify()
    scheduleRefresh()
    setupTabClosePolicy()
  },

  get(): string | null {
    return inMemoryToken
  },

  set(token: string | null): void {
    inMemoryToken = token
    writePersisted(token)
    try {
      broadcast?.postMessage({ type: 'token', token })
    } catch {
      /* ignore */
    }
    notify()
    scheduleRefresh()
  },

  clear(): void {
    inMemoryToken = null
    writePersisted(null)
    try {
      broadcast?.postMessage({ type: 'token', token: null })
    } catch {
      /* ignore */
    }
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
    notify()
  },

  decode(): DecodedJwt | null {
    return decode(inMemoryToken)
  },

  getUser(): AuthUser | null {
    const p = decode(inMemoryToken)
    if (!p) return null
    return {
      userId: p.userId || p.sub || '',
      email: p.email || '',
      tenantId: p.tenant_id || '',
      role: p.role || '',
      permissions: p.permissions || [],
      isImpersonating: !!p.is_impersonating,
      originalSub: p.original_sub,
      exp: p.exp,
    }
  },

  hasRole(...roles: string[]): boolean {
    const u = this.getUser()
    return !!u && roles.includes(u.role)
  },

  hasPermission(code: string): boolean {
    const u = this.getUser()
    if (!u) return false
    // Platform-tenant super_admin (Bokapsys staff) bypass.
    if (u.role === 'super_admin' && u.tenantId === PLATFORM_TENANT_ID) return true
    return u.permissions.includes(code)
  },

  isPlatformAdmin(): boolean {
    const u = this.getUser()
    return !!u && u.role === 'super_admin' && u.tenantId === PLATFORM_TENANT_ID
  },

  isExpired(): boolean {
    const u = this.getUser()
    if (!u || !u.exp) return true
    return Date.now() >= u.exp * 1000
  },

  /**
   * Single-flight silent refresh. Returns true if the new token was stored,
   * false on any failure. The HttpOnly refresh cookie travels automatically
   * because the request is same-origin.
   */
  async refreshNow(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight
    refreshInFlight = (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
        if (!res.ok) return false
        const data = (await res.json()) as { token?: string; sessionTimeout?: string }
        if (!data?.token) return false
        if (PERSIST_TOKEN_ACROSS_RELOADS && data.sessionTimeout) {
          try {
            localStorage.setItem(SESSION_TIMEOUT_KEY, data.sessionTimeout)
          } catch {
            /* ignore */
          }
        }
        tokenManager.set(data.token)
        return true
      } catch {
        return false
      } finally {
        refreshInFlight = null
      }
    })()
    return refreshInFlight
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },

  PLATFORM_TENANT_ID,
}

function scheduleRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
  const p = decode(inMemoryToken)
  if (!p?.exp) return
  const nowSec = Math.floor(Date.now() / 1000)
  let delaySec = p.exp - nowSec - REFRESH_LEAD_SECONDS
  if (delaySec < MIN_SCHEDULE_SECONDS) delaySec = MIN_SCHEDULE_SECONDS
  refreshTimer = setTimeout(() => {
    void tokenManager.refreshNow()
  }, delaySec * 1000)
}

function setupTabClosePolicy(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(TAB_ALIVE_KEY, '1')
  } catch {
    /* ignore */
  }
  if (
    PERSIST_TOKEN_ACROSS_RELOADS &&
    localStorage.getItem(SESSION_TIMEOUT_KEY) === 'tab_close' &&
    !sessionStorage.getItem(TAB_ALIVE_KEY)
  ) {
    tokenManager.clear()
    void fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(
      () => undefined,
    )
  }
}

export { SUPER_ADMIN_TOKEN_KEY }

// Auto-seed the in-memory JWT from localStorage as soon as this module
// evaluates. Must happen BEFORE the router module is imported, because
// React Router v6.4+ executes route loaders synchronously during
// `createBrowserRouter()` - loaders that call `tokenManager.get()` would
// otherwise see a null token on the first page load after an OAuth
// handoff or a bookmark, and bounce the user to /login.
//
// main.tsx imports this module before `./App` (which imports `./router`),
// so module-init order guarantees this runs first.
if (typeof window !== 'undefined') {
  tokenManager.bootstrap()
}
